import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { LegalDocument, CreateLegalDocDTO, Section } from "../types/legal";
import AIService from "./AIService";

const LEGAL_DOCS_COLLECTION = "legalDocuments";

class LegalService {
  /**
   * Generate a unique document ID
   */
  private generateDocumentId(): string {
    return `doc_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  /**
   * Convert Firestore timestamp to Date
   */
  private convertTimestampToDate(timestamp: any): Date {
    if (timestamp instanceof Timestamp) {
      return timestamp.toDate();
    }
    if (timestamp?.toDate && typeof timestamp.toDate === "function") {
      return timestamp.toDate();
    }
    return new Date(timestamp);
  }

  private mapFirestoreDocToLegalDocument(
    data: any,
    firestoreId?: string,
  ): LegalDocument {
    return {
      firestoreId,
      documentId: data.documentId,
      appId: data.appId,
      appPublicIdentifiers: Array.isArray(data.appPublicIdentifiers)
        ? data.appPublicIdentifiers
        : [data.appId].filter(Boolean),
      type: data.type,
      content: data.content,
      htmlContent: data.htmlContent,
      effectiveDate: this.convertTimestampToDate(data.effectiveDate),
      lastUpdated: this.convertTimestampToDate(data.lastUpdated),
      version: data.version,
      versionNumber: Number(data.versionNumber || 1),
      isLive: Boolean(data.isLive),
      sections: data.sections || [],
      userId: data.userId,
      modelUsed: data.modelUsed,
      providerUsed: data.providerUsed,
    } as LegalDocument;
  }

  /**
   * Get all legal documents for an app
   */
  async getDocumentsByAppId(
    appId: string,
    userId: string,
  ): Promise<LegalDocument[]> {
    try {
      const docsRef = collection(db, LEGAL_DOCS_COLLECTION);
      const q = query(
        docsRef,
        where("appId", "==", appId),
        where("userId", "==", userId),
      );
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs
        .map((doc) => this.mapFirestoreDocToLegalDocument(doc.data(), doc.id))
        .sort((a, b) => {
          if (a.type !== b.type) {
            return a.type.localeCompare(b.type);
          }
          return (b.versionNumber || 1) - (a.versionNumber || 1);
        });
    } catch (error) {
      console.error("Error fetching legal documents:", error);
      throw new Error("Failed to fetch legal documents");
    }
  }

  /**
   * Get a specific document by ID
   */
  async getDocumentById(
    documentId: string,
    userId: string,
  ): Promise<LegalDocument | null> {
    try {
      const docsRef = collection(db, LEGAL_DOCS_COLLECTION);
      const q = query(
        docsRef,
        where("documentId", "==", documentId),
        where("userId", "==", userId),
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return null;
      }

      const firstDoc = querySnapshot.docs[0];
      return this.mapFirestoreDocToLegalDocument(firstDoc.data(), firstDoc.id);
    } catch (error) {
      console.error("Error fetching document:", error);
      throw new Error("Failed to fetch document");
    }
  }

  /**
   * Generate AI-powered legal document
   */
  async generateDocument(
    userId: string,
    docRequest: CreateLegalDocDTO,
    appName: string,
    appDescription: string,
    appPublicIdentifiers: string[] = [],
  ): Promise<LegalDocument> {
    try {
      const documentId = this.generateDocumentId();
      const now = serverTimestamp();
      const nextVersionNumber = await this.getNextVersionNumber(
        docRequest.appId,
        userId,
        docRequest.type,
      );

      const { content, htmlContent, sections, modelUsed, providerUsed } =
        await this.generateContentWithAI(docRequest, appName, appDescription);

      const normalizedIdentifiers = Array.from(
        new Set(
          [docRequest.appId, ...appPublicIdentifiers]
            .map((value) => String(value || "").trim())
            .filter(Boolean),
        ),
      );

      const newDocument = {
        documentId,
        appId: docRequest.appId,
        appPublicIdentifiers: normalizedIdentifiers,
        type: docRequest.type,
        content,
        htmlContent,
        effectiveDate: now,
        lastUpdated: now,
        version: `${nextVersionNumber}.0`,
        versionNumber: nextVersionNumber,
        isLive: true,
        sections,
        userId,
        modelUsed,
        providerUsed,
      };

      await this.unsetLiveForAppType(docRequest.appId, userId, docRequest.type);
      await addDoc(collection(db, LEGAL_DOCS_COLLECTION), newDocument);

      return {
        ...newDocument,
        effectiveDate: new Date(),
        lastUpdated: new Date(),
      } as LegalDocument;
    } catch (error) {
      console.error("Error generating document:", error);
      throw new Error("Failed to generate document");
    }
  }

  async getPublicDocumentByAppId(
    appParam: string,
    type: LegalDocument["type"],
  ): Promise<LegalDocument | null> {
    const normalizedAppParam = String(appParam || "").trim();
    if (!normalizedAppParam) {
      return null;
    }

    const docsRef = collection(db, LEGAL_DOCS_COLLECTION);
    const candidates: LegalDocument[] = [];

    const exactAppSnapshot = await getDocs(
      query(docsRef, where("appId", "==", normalizedAppParam)),
    );
    exactAppSnapshot.docs.forEach((doc) => {
      const mapped = this.mapFirestoreDocToLegalDocument(doc.data(), doc.id);
      if (mapped.type === type) {
        candidates.push(mapped);
      }
    });

    if (candidates.length === 0) {
      const identifierSnapshot = await getDocs(
        query(
          docsRef,
          where("appPublicIdentifiers", "array-contains", normalizedAppParam),
        ),
      );
      identifierSnapshot.docs.forEach((doc) => {
        const mapped = this.mapFirestoreDocToLegalDocument(doc.data(), doc.id);
        if (mapped.type === type) {
          candidates.push(mapped);
        }
      });
    }

    if (candidates.length === 0) {
      return null;
    }

    const liveDoc = candidates.find((doc) => doc.isLive);
    if (liveDoc) {
      return liveDoc;
    }

    return candidates.sort((a, b) => {
      const versionDelta = (b.versionNumber || 1) - (a.versionNumber || 1);
      if (versionDelta !== 0) {
        return versionDelta;
      }
      return b.lastUpdated.getTime() - a.lastUpdated.getTime();
    })[0];
  }

  async updateDocumentContent(
    documentId: string,
    userId: string,
    content: string,
  ): Promise<void> {
    const docsRef = collection(db, LEGAL_DOCS_COLLECTION);
    const q = query(
      docsRef,
      where("documentId", "==", documentId),
      where("userId", "==", userId),
    );
    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) {
      throw new Error("Document not found");
    }

    const sections = this.extractSectionsFromMarkdown(content);
    const htmlContent = this.markdownToHtml(content);
    await updateDoc(querySnapshot.docs[0].ref, {
      content,
      htmlContent,
      sections,
      lastUpdated: serverTimestamp(),
    });
  }

  async setDocumentLive(documentId: string, userId: string): Promise<void> {
    const docsRef = collection(db, LEGAL_DOCS_COLLECTION);
    const q = query(
      docsRef,
      where("documentId", "==", documentId),
      where("userId", "==", userId),
    );
    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) {
      throw new Error("Document not found");
    }

    const target = querySnapshot.docs[0].data();
    await this.unsetLiveForAppType(target.appId, userId, target.type);
    await updateDoc(querySnapshot.docs[0].ref, {
      isLive: true,
      lastUpdated: serverTimestamp(),
    });
  }

  /**
   * Delete a legal document
   */
  async deleteDocument(documentId: string, userId: string): Promise<void> {
    try {
      const docsRef = collection(db, LEGAL_DOCS_COLLECTION);
      const q = query(
        docsRef,
        where("documentId", "==", documentId),
        where("userId", "==", userId),
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        throw new Error("Document not found");
      }

      const targetDoc = querySnapshot.docs[0];
      const targetData = targetDoc.data();
      const wasLive = Boolean(targetData.isLive);
      const targetType = targetData.type;
      const targetAppId = targetData.appId;

      const docRef = targetDoc.ref;
      await deleteDoc(docRef);

      if (wasLive) {
        await this.promoteLatestAsLive(targetAppId, userId, targetType);
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      throw new Error("Failed to delete document");
    }
  }

  /**
   * AI Content Generation (Mock for now - will integrate with Gemini)
   */
  private async generateContentWithAI(
    docRequest: CreateLegalDocDTO,
    appName: string,
    appDescription: string,
  ): Promise<{
    content: string;
    htmlContent: string;
    sections: Section[];
    modelUsed?: string;
    providerUsed?: "gemini" | "openai";
  }> {
    const prompt = this.buildLegalPrompt(docRequest, appName, appDescription);
    const aiResponse = await AIService.generateText(prompt, {
      model: docRequest.generationModel?.trim() || undefined,
      provider: docRequest.generationProvider,
      temperature: 0.3,
      maxTokens: 4096,
      systemInstruction:
        "You are a legal drafting assistant for app privacy policies and terms. Output only markdown. Use clear numbered sections and practical legal language.",
    });

    const content = aiResponse.text?.trim();
    if (!content) {
      throw new Error("AI did not return legal document content.");
    }

    const sanitizedContent = this.sanitizeGeneratedLegalContent(
      content,
      docRequest,
    );

    return {
      content: sanitizedContent,
      htmlContent: this.markdownToHtml(sanitizedContent),
      sections: this.extractSectionsFromMarkdown(sanitizedContent),
      modelUsed: aiResponse.model,
      providerUsed: this.inferProviderFromModel(aiResponse.model),
    };
  }

  private buildLegalPrompt(
    docRequest: CreateLegalDocDTO,
    appName: string,
    appDescription: string,
  ): string {
    const typeName = this.getDocumentTypeName(docRequest.type);
    const personalInfo = docRequest.dataCollection.personalInfo.length
      ? docRequest.dataCollection.personalInfo.join(", ")
      : "None specified";
    const thirdParty = docRequest.dataCollection.thirdPartyServices.length
      ? docRequest.dataCollection.thirdPartyServices.join(", ")
      : "None specified";

    return [
      `Generate a complete ${typeName} in markdown for this app.`,
      "",
      "Requirements:",
      "- Include a title, effective date, and numbered sections.",
      "- Keep language professional and app-store suitable.",
      "- Include jurisdiction-specific rights and compliance notes.",
      "- Include contact details and policy update clause.",
      "- Never include placeholder text like [Insert ... Here] in the final output.",
      "- If company address is not provided, omit address-specific placeholders/sections.",
      "",
      "App details:",
      `- App name: ${appName}`,
      `- App description: ${appDescription || "Not provided"}`,
      `- Company name: ${docRequest.companyName}`,
      `- Company address: ${docRequest.companyAddress || "Not provided"}`,
      `- Contact email: ${docRequest.contactEmail}`,
      `- Website: ${docRequest.website || "Not provided"}`,
      `- Jurisdiction: ${docRequest.jurisdiction}`,
      `- App type: ${docRequest.appType}`,
      "",
      "Data collection:",
      `- Personal info: ${personalInfo}`,
      `- Third-party services: ${thirdParty}`,
      `- Uses cookies: ${docRequest.dataCollection.cookies ? "Yes" : "No"}`,
      `- Uses analytics: ${docRequest.dataCollection.analytics ? "Yes" : "No"}`,
      `- Uses advertising: ${
        docRequest.dataCollection.advertising ? "Yes" : "No"
      }`,
      "",
      "App features:",
      `- User accounts: ${docRequest.features.userAccounts ? "Yes" : "No"}`,
      `- In-app purchases: ${
        docRequest.features.inAppPurchases ? "Yes" : "No"
      }`,
      `- User-generated content: ${
        docRequest.features.userGeneratedContent ? "Yes" : "No"
      }`,
      `- Location services: ${docRequest.features.location ? "Yes" : "No"}`,
      `- Push notifications: ${
        docRequest.features.pushNotifications ? "Yes" : "No"
      }`,
      "",
      "Output markdown only. Do not output JSON or code blocks.",
    ].join("\n");
  }

  private sanitizeGeneratedLegalContent(
    markdown: string,
    docRequest: CreateLegalDocDTO,
  ): string {
    let sanitized = String(markdown || "");

    // Remove unresolved bracket placeholders, e.g. [Insert Company Address Here]
    sanitized = sanitized.replace(/\[\s*Insert[^[\]]*?\]/gi, "").trim();

    // If no company address was provided, remove address placeholder remnants.
    if (!String(docRequest.companyAddress || "").trim()) {
      sanitized = sanitized
        .replace(/^\s*Company Address\s*:\s*$/gim, "")
        .replace(/^\s*Address\s*:\s*$/gim, "")
        .replace(/^\s*Mailing Address\s*:\s*$/gim, "");
    }

    // Collapse excessive blank lines introduced by sanitization.
    sanitized = sanitized.replace(/\n{3,}/g, "\n\n").trim();

    return sanitized;
  }

  /**
   * Simple markdown to HTML converter
   */
  private markdownToHtml(markdown: string): string {
    let html = markdown;

    // Headers
    html = html.replace(/^### (.*$)/gim, "<h3>$1</h3>");
    html = html.replace(/^## (.*$)/gim, "<h2>$1</h2>");
    html = html.replace(/^# (.*$)/gim, "<h1>$1</h1>");

    // Bold
    html = html.replace(/\*\*(.*?)\*\*/gim, "<strong>$1</strong>");

    // Line breaks
    html = html.replace(/\n\n/g, "</p><p>");
    html = html.replace(/^(.+)$/gim, "<p>$1</p>");

    // HR
    html = html.replace(/---/g, "<hr>");

    return html;
  }

  private extractSectionsFromMarkdown(markdown: string): Section[] {
    const matches = Array.from(markdown.matchAll(/^##\s+(.+)$/gim));
    if (!matches.length) {
      return [];
    }

    return matches.map((match, index) => {
      const title = match[1].trim();
      const start = match.index ?? 0;
      const nextStart = matches[index + 1]?.index ?? markdown.length;
      const rawBlock = markdown.slice(start, nextStart);
      const content = rawBlock.replace(/^##\s+.+$/m, "").trim();
      return {
        title,
        content,
        order: index + 1,
      };
    });
  }

  private inferProviderFromModel(model?: string): "gemini" | "openai" {
    const normalized = String(model || "").toLowerCase();
    if (normalized.includes("gemini") || normalized.includes("veo")) {
      return "gemini";
    }
    return "openai";
  }

  private async getNextVersionNumber(
    appId: string,
    userId: string,
    type: LegalDocument["type"],
  ): Promise<number> {
    const docsRef = collection(db, LEGAL_DOCS_COLLECTION);
    const q = query(
      docsRef,
      where("appId", "==", appId),
      where("userId", "==", userId),
      where("type", "==", type),
    );
    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) {
      return 1;
    }

    let highestVersion = 0;
    querySnapshot.docs.forEach((doc) => {
      const data = doc.data();
      const versionNumber = Number(data.versionNumber || data.version || 0);
      if (Number.isFinite(versionNumber) && versionNumber > highestVersion) {
        highestVersion = versionNumber;
      }
    });
    return highestVersion + 1;
  }

  private async unsetLiveForAppType(
    appId: string,
    userId: string,
    type: LegalDocument["type"],
  ): Promise<void> {
    const docsRef = collection(db, LEGAL_DOCS_COLLECTION);
    const q = query(
      docsRef,
      where("appId", "==", appId),
      where("userId", "==", userId),
      where("type", "==", type),
    );
    const querySnapshot = await getDocs(q);
    await Promise.all(
      querySnapshot.docs.map((doc) => updateDoc(doc.ref, { isLive: false })),
    );
  }

  private async promoteLatestAsLive(
    appId: string,
    userId: string,
    type: LegalDocument["type"],
  ): Promise<void> {
    const docsRef = collection(db, LEGAL_DOCS_COLLECTION);
    const q = query(
      docsRef,
      where("appId", "==", appId),
      where("userId", "==", userId),
      where("type", "==", type),
    );
    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) {
      return;
    }

    const sorted = querySnapshot.docs
      .map((doc) => ({ ref: doc.ref, data: doc.data() }))
      .sort((a, b) => {
        const av = Number(a.data.versionNumber || a.data.version || 1);
        const bv = Number(b.data.versionNumber || b.data.version || 1);
        return bv - av;
      });

    await Promise.all(
      sorted.map((entry, index) =>
        updateDoc(entry.ref, { isLive: index === 0 }),
      ),
    );
  }

  /**
   * Get document type display name
   */
  getDocumentTypeName(type: string): string {
    const names: Record<string, string> = {
      tos: "Terms of Service",
      privacy: "Privacy Policy",
      eula: "End User License Agreement",
      cookie: "Cookie Policy",
      dpa: "Data Processing Agreement",
    };
    return names[type] || type;
  }
}

export default new LegalService();
